<script setup>
import { onMounted } from 'vue'
import Header from '@/components/Header.vue'
import DesignFooter from '@/components/DesignFooter.vue'
import { setTrack } from '@/api/login'

const asset = (name) => `${import.meta.env.BASE_URL}image/playground-redesign/${name}`
const experienceUrl = 'https://www.quectel.com.cn/contact?pro=QUkgUGxheWdyb3VuZA&tagStr=NGExMDllYjU2NDc3OGZjZg'
const documentationUrl = 'https://www.quectel.com.cn/quectel-pi/documentation-center'

const capabilities = [
  {
    icon: 'grid.svg',
    title: '10 个预置算法模块',
    description: '覆盖人脸检测、手势检测、OCR、人体分割、安全帽检测、DMS 驾驶员监控等典型视觉场景。',
  },
  {
    icon: 'camera.svg',
    title: '图片、视频、摄像头三种输入',
    description: '导入关键样本、真实录像或连接 USB 摄像头，按验证场景快速切换。',
  },
  {
    icon: 'chart.svg',
    title: '端侧监控与结果导出',
    description: '实时查看 RAM、ROM、CPU、NPU 占用，并导出图片、JSON 或 TXT 推理结果。',
  },
]

const algorithms = [
  { image: 'face.webp', title: '人脸检测', description: '人脸框与关键点' },
  { image: 'gesture.webp', title: '手势检测', description: '手部关键点识别' },
  { image: 'emotion.webp', title: '情绪与性别', description: '人脸属性识别' },
  { image: 'posture.webp', title: '坐姿检测', description: '关键点姿态分析' },
  { image: 'crowd.webp', title: '人群密度', description: '客流与密度估算' },
  { image: 'ocr.webp', title: 'OCR 文字识别', description: '图像文字提取' },
  { image: 'mask.webp', title: '口罩检测', description: '佩戴状态识别' },
  { image: 'helmet.webp', title: '安全帽检测', description: '作业安全检查' },
  { image: 'segmentation.webp', title: '人像分割', description: '前景区域提取' },
  { image: 'dms.webp', title: 'DMS 驾驶员监控', description: '驾驶状态分析' },
]

// 不等待埋点，且捕获同步异常与请求失败，保持链接原生跳转行为。
const track = (position) => {
  void Promise.resolve()
    .then(() => setTrack({ position, page: 'ai_playground' }))
    .catch(() => {})
}

onMounted(() => track('ai_playground'))
</script>

<template>
  <Header design-always hide-banner />

  <main class="playground-page">
    <section class="playground-hero" aria-labelledby="playground-title">
      <div class="playground-container hero-layout">
        <div class="hero-copy">
          <h1 id="playground-title">AI Playground 用户体验平台</h1>
          <p class="hero-description">面向 Quectel Pi 的端侧 AI 推理演示平台。内置 10 个场景化算法模块，连接摄像头或导入素材即可验证，帮助开发者快速判断端侧 AI 是否适配真实业务。</p>
          <div class="hero-actions">
            <a class="playground-button primary-button" :href="experienceUrl" target="_blank" rel="noopener noreferrer" @click="track('consultation')">申请体验</a>
            <a class="playground-button secondary-button" v-track="'ai_playground > view_docs'" :href="documentationUrl" target="_blank" rel="noopener noreferrer">查看文档</a>
          </div>
        </div>
        <img class="hero-image" :src="asset('hero.webp')" alt="Quectel Pi H1 AI Playground 端侧推理演示平台" fetchpriority="high" />
      </div>
    </section>

    <section class="playground-section overview-section" aria-labelledby="overview-title">
      <div class="playground-container">
        <div class="section-heading">
          <h2 id="overview-title">从预置算法，到现场可见的推理结果</h2>
          <p>无需搭建独立推理框架或编写演示代码，通过统一界面体验端侧 NPU 加速下的模型效果、性能表现与输出结果。</p>
        </div>
        <div class="overview-layout">
          <figure class="overview-figure">
            <img :src="asset('overview.webp')" alt="AI Playground 统一推理界面及端侧性能监控" loading="lazy" />
            <figcaption>
              <strong>QUECTEL PI H1</strong>
              <span>端侧 NPU 推理 · 本地运行 · 实时响应</span>
            </figcaption>
          </figure>
          <ul class="capability-list">
            <li v-for="capability in capabilities" :key="capability.title" class="capability-item">
              <span class="capability-icon"><img :src="asset(capability.icon)" alt="" loading="lazy" width="24" height="24" /></span>
              <div>
                <h3>{{ capability.title }}</h3>
                <p>{{ capability.description }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>

    <section class="playground-section algorithms-section" aria-labelledby="algorithms-title">
      <div class="playground-container">
        <div class="section-heading">
          <h2 id="algorithms-title">覆盖主流视觉 AI 场景</h2>
          <p>选择接近业务的算法模块，导入自有素材，即可完成端侧效果的初步验证。</p>
        </div>
        <div class="algorithm-grid">
          <article v-for="algorithm in algorithms" :key="algorithm.image" class="algorithm-card">
            <img class="algorithm-image" :src="asset(algorithm.image)" :alt="`${algorithm.title}效果展示`" loading="lazy" width="272" height="176" />
            <div class="algorithm-content">
              <h3>{{ algorithm.title }}</h3>
              <p>{{ algorithm.description }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>
  </main>

  <DesignFooter />
</template>

<style scoped>
.playground-page {
  width: 100%;
  min-width: 0;
  color: #202733;
  line-height: 1.5;
}

.playground-page,
.playground-page *,
.playground-page *::before,
.playground-page *::after {
  box-sizing: border-box;
}

.playground-page h1,
.playground-page h2,
.playground-page h3,
.playground-page p,
.playground-page figure,
.playground-page ul {
  margin: 0;
}

.playground-page img { display: block; max-width: 100%; }
.playground-container { width: min(1440px, calc(100% - 48px)); margin: 0 auto; }
.playground-hero { display: flex; align-items: center; min-height: 416px; padding: 40px 0; background: #f4f5f7; }
.hero-layout { display: grid; grid-template-columns: 1.1fr 1fr; align-items: center; gap: 40px; }
.hero-copy { min-width: 0; }
.hero-copy h1 { color: #202733; font-size: 30px; font-weight: 600; line-height: 1.35; overflow-wrap: anywhere; }
.hero-description { margin-top: 20px !important; color: #596474; font-size: 16px; line-height: 1.8; }
.hero-actions { display: flex; flex-wrap: wrap; gap: 16px; margin-top: 28px; }
.playground-button { display: inline-flex; justify-content: center; align-items: center; min-height: 44px; padding: 10px 28px; border: 1px solid transparent; border-radius: 4px; font-size: 16px; font-weight: 500; text-decoration: none; transition: background-color 150ms ease; }
.primary-button { background: #c81832; border-color: #c81832; color: #fff; }
.primary-button:hover { background: #ab1229; border-color: #ab1229; color: #fff; }
.secondary-button { background: #fff; border-color: #cbd2dc; color: #303b4b; }
.secondary-button:hover { background: #f7f9fc; color: #202733; }
.playground-button:focus-visible { outline: 3px solid #1478d4; outline-offset: 4px; }
.hero-image { width: 100%; height: auto; max-height: 360px; object-fit: contain; }
.playground-section { padding: 64px 0; }
.overview-section { background: #fff; }
.section-heading { margin-bottom: 40px; text-align: center; }
.section-heading h2 { color: #202733; font-size: 24px; font-weight: 600; line-height: 1.4; }
.section-heading p { max-width: 960px; margin: 16px auto 0; color: #657184; font-size: 16px; line-height: 1.8; }
.overview-layout { display: grid; grid-template-columns: 1.1fr 1fr; gap: 64px; align-items: center; }
.overview-figure { min-width: 0; }
.overview-figure > img { width: 100%; height: auto; object-fit: contain; }
.overview-figure figcaption { display: flex; flex-direction: column; align-items: center; gap: 6px; margin-top: 20px; text-align: center; font-size: 14px; color: #657184; }
.overview-figure figcaption strong { font-weight: 600; letter-spacing: 1px; color: #303b4b; }
.capability-list { display: grid; gap: 32px; padding: 0; list-style: none; min-width: 0; }
.capability-item { display: flex; align-items: flex-start; gap: 16px; }
.capability-item > div { min-width: 0; }
.capability-icon { display: flex; align-items: center; justify-content: center; flex: 0 0 48px; width: 48px; height: 48px; border-radius: 50%; background: #eef3fb; }
.capability-item h3 { margin-bottom: 8px; color: #202733; font-size: 20px; font-weight: 600; line-height: 1.5; }
.capability-item p { color: #657184; font-size: 16px; line-height: 1.8; }
.algorithms-section { background: #f4f7fb; }
.algorithm-grid { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 20px; }
.algorithm-card { display: flex; flex-direction: column; min-width: 0; overflow: hidden; border: 1px solid #dce2ea; border-radius: 8px; background: #fff; box-shadow: 0 3px 12px rgb(26 46 76 / 4%); }
.algorithm-image { width: 100%; height: auto; aspect-ratio: 272 / 176; object-fit: cover; }
.algorithm-content { flex: 1; padding: 20px; }
.algorithm-content h3 { color: #202733; font-size: 18px; font-weight: 600; line-height: 1.5; }
.algorithm-content p { margin-top: 8px; color: #657184; font-size: 14px; line-height: 24px; }

@media (min-width: 768px) {
  .hero-copy h1 { font-size: 36px; }
  .hero-description { font-size: 18px; }
  .section-heading h2 { font-size: 26px; }
}

@media (min-width: 992px) {
  .hero-copy h1 { font-size: 40px; }
  .section-heading h2 { font-size: 28px; }
}

@media (min-width: 1200px) {
  .hero-copy h1 { font-size: 44px; }
  .section-heading h2 { font-size: 30px; }
}

@media (min-width: 1400px) {
  .section-heading h2 { font-size: 32px; }
}

@media (max-width: 1199px) {
  .algorithm-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
  .overview-layout { gap: 32px; }
}

@media (max-width: 991px) {
  .hero-layout, .overview-layout { grid-template-columns: minmax(0, 1fr); gap: 32px; }
  .hero-image { max-width: 640px; margin: 0 auto; }
  .overview-figure { width: 100%; max-width: 720px; margin: 0 auto; }
  .algorithm-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 767px) {
  .playground-container { width: calc(100% - 32px); }
  .playground-section { padding: 40px 0; }
  .section-heading { margin-bottom: 28px; }
  .capability-list { gap: 24px; }
  .capability-item { gap: 12px; }
  .algorithm-content { padding: 16px; }
}

@media (max-width: 575px) {
  .algorithm-grid { grid-template-columns: minmax(0, 1fr); }
  .hero-actions { gap: 12px; }
  .playground-button { padding-inline: 24px; }
}
</style>
